/**
 * CLN higher-timeframe context band — the stamping walk, extracted.
 *
 * The band runs the same regression (computeClnBars) on the mapped higher
 * timeframe and stamps each lower-timeframe bar with the HTF body covering it,
 * so the level holds flat across the HTF window and rises over a single LTF bar
 * at the boundary. That walk was inline in TradingChart and nothing outside the
 * chart could read it, which made the band untestable and unusable as a rule
 * input even though it is on screen by default for this account.
 *
 * WHAT IS AND IS NOT HERE. The stamping is here. The run-splitting into
 * BandPrimitive segments is NOT — that is rendering, it has no meaning off the
 * chart, and it depends on lightweight-charts types.
 *
 * JOINED ON BAR TIME, NEVER ON INDEX. The two series have unrelated lengths and
 * unrelated grids; a length check here is not a safety net, it is a silent
 * feature-disabling bug waiting to happen. Both series must be ascending in
 * time, which every caller's already is.
 *
 * THE FORMING BAR is reported, not hidden. The chart paints it differently
 * (lighter wash, dashed edge) because its body still moves; anything acting on
 * the band rather than drawing it must drop `forming` stamps outright, since a
 * value that repaints is not something a decision can rest on.
 */
import { CandleRow } from '../components/charts/TradingChart';
import { computeClnBars, CLN_DEFAULT_LENGTH } from './clnCandles';

export interface ClnHtfStamp {
  /** The LTF bar's time. */
  time: number;
  /** The covering HTF regression body's top and bottom. */
  upper: number;
  lower: number;
  /** Regression direction of the covering HTF bar. */
  up: boolean;
  /** The covering HTF bar had not closed yet. */
  forming: boolean;
}

/**
 * @param ltfBars   the chart/engine's own bars, ascending
 * @param htfBars   the mapped higher-timeframe bars, ascending. Pass the series
 *                  you intend to draw: the caller decides whether the still-open
 *                  HTF bar is included.
 * @param htfMinutes the HTF bar size, used to know when a bar's window ends
 * @param closedLen how many leading `htfBars` have CLOSED. Stamps sourced at or
 *                  beyond this index are marked `forming`.
 * @param clnLength regression length (settings.candle.clnLength)
 */
export function computeClnHtfStamps(
  ltfBars: CandleRow[],
  htfBars: CandleRow[],
  htfMinutes: number,
  closedLen: number,
  clnLength: number = CLN_DEFAULT_LENGTH,
): ClnHtfStamp[] {
  if (!ltfBars?.length || !htfBars?.length || !(htfMinutes > 0)) return [];
  const bodies = computeClnBars(htfBars, clnLength);
  if (bodies.length < 2) return [];

  const durSec = htfMinutes * 60;
  const out: ClnHtfStamp[] = [];
  // Walking both series once keeps this O(n) instead of a lookup per bar.
  let k = 0;
  for (const c of ltfBars) {
    const t = c.time as number;
    while (k + 1 < bodies.length && bodies[k + 1].time <= t) k++;
    const b = bodies[k];
    if (!b || b.time > t) continue;            // before the first HTF bar opens
    const forming = k >= closedLen;
    // Past this HTF bar's own window there is no context to draw — EXCEPT for
    // the still-forming bar, whose window is open by definition.
    if (!forming && t >= b.time + durSec) continue;
    out.push({ time: t, upper: b.lrBodyHigh, lower: b.lrBodyLow, up: b.up, forming });
  }
  return out;
}
